// ── JobSpec Authoring (Layer 3b) ──
// Draft model + change log for editing the photo + checklist requirements
// of a JobSpec. Pure data layer — no React, no I/O.
//
// Hard constraints (locked by Layer 3b scope):
//   • No phase name / order / count editing — phases come from the source spec
//     and stay as-is. The editor only mutates each phase's requiredPhotos
//     list and checklistItems list.
//   • No BOM / SKU editing — billOfMaterials is copied verbatim on publish.
//   • No proof-policy logic here — proof gating still flows through
//     installerProof.ts. This file only changes WHAT the spec requires;
//     ProofPolicy controls HOW MISSING items are enforced.
//   • No critical flag on checklist items (deferred). Items remain
//     { id, label, required }. `required: false` means "informational only";
//     proof eval ignores non-required items.
//   • Bound jobs are NEVER mutated. Publishing creates a new spec version;
//     existing BoundJobSpec snapshots stay frozen.
//
// Storage shape:
//   • Drafts and published overlay versions live in a single overlay store
//     keyed by specId. Persisted to localStorage by the context provider.
//   • Change log capped at 50 entries per spec (decision recorded in plan).

import type { UserRole } from "./RoleContext";
import type {
  JobSpec,
  JobSpecPhase,
  JobSpecChecklistItem,
  RequiredPhotoType,
} from "./jobSpec";

// ── Types ───────────────────────────────────────────────────────────────────

/**
 * Editable checklist item. Adds `required` flag controlling whether proof
 * eval treats this item as gating. (No `critical` flag — deferred.)
 *
 * Backwards-compat: existing JobSpec checklist items in the registry have
 * no `required` field. The authoring layer treats absent `required` as
 * `true` (preserves current proof-eval behavior).
 */
export interface AuthoringChecklistItem extends JobSpecChecklistItem {
  required: boolean;
}

export interface AuthoringPhase {
  name: JobSpecPhase["name"];
  label: string;
  responsibleTeam?: JobSpecPhase["responsibleTeam"];
  requiredPhotos: RequiredPhotoType[];
  checklistItems: AuthoringChecklistItem[];
}

export interface JobSpecChangeLogEntry {
  id: string;
  changedAt: string; // ISO
  changedBy: UserRole;
  /** Summary of what changed, for the panel footer. */
  summary: string;
  /** Optional reason supplied at publish time. */
  reason?: string;
  /**
   * Action discriminator. `draft_saved` = local stage; `published` = wrote
   * a new overlay version that future binds will resolve to.
   */
  action: "draft_saved" | "published" | "discarded";
  /** Version number written by this action (publish only). */
  publishedVersion?: number;
}

export interface JobSpecAuthoringRecord {
  specId: string;
  /** Current draft, or undefined if no edits in flight. */
  draft?: AuthoringSpec;
  /**
   * Published overlay versions. These supersede the registry for
   * `getCanonicalSpecForArchetype` lookups (binding new jobs). Existing
   * bound jobs use their own frozen snapshot — unaffected.
   */
  publishedVersions: AuthoringSpec[];
  /** Append-only, capped at 50 entries. Newest first. */
  changeLog: JobSpecChangeLogEntry[];
}

/**
 * Authoring view of a spec — same shape as JobSpec but with
 * AuthoringChecklistItem (carries `required` flag).
 */
export interface AuthoringSpec
  extends Omit<JobSpec, "phases"> {
  phases: AuthoringPhase[];
}

export const CHANGE_LOG_CAP = 50;

// ── Role gate ───────────────────────────────────────────────────────────────
// Reuses the same authority shape as the InstallerExecutionStrictness
// editors per scope ("no new permission keys").
export const SPEC_AUTHORING_EDITORS: UserRole[] = [
  "super_admin",
  "owner",
  "admin",
  "ops_manager",
  "head_of_installation",
  "it_staff",
];

export function canEditJobSpec(role: UserRole): boolean {
  return SPEC_AUTHORING_EDITORS.includes(role);
}

// ── Conversions ─────────────────────────────────────────────────────────────

/** Convert a registry JobSpec into an editable AuthoringSpec (deep clone). */
export function toAuthoringSpec(spec: JobSpec): AuthoringSpec {
  return {
    ...spec,
    phases: spec.phases.map((p) => ({
      name: p.name,
      label: p.label,
      responsibleTeam: p.responsibleTeam,
      requiredPhotos: [...p.requiredPhotos],
      checklistItems: p.checklistItems.map((c) => ({
        id: c.id,
        label: c.label,
        // Legacy items had no `required` field — they were always gating,
        // so default to true for parity.
        required: (c as Partial<AuthoringChecklistItem>).required ?? true,
      })),
    })),
    billOfMaterials: spec.billOfMaterials.map((b) => ({ ...b })),
  };
}

/**
 * Project an AuthoringSpec back into a registry-shaped JobSpec.
 *
 * IMPORTANT: existing JobSpecPhase.checklistItems is `{ id, label }`. We
 * only emit items whose `required` flag is true into the published spec's
 * required list — non-required items are dropped, since the broader system
 * treats every entry in `checklistItems` as gating. This keeps proof eval
 * (installerProof.ts) honest without modifying its logic.
 *
 * If the system later wants advisory-only items in the spec, we'll extend
 * JobSpecChecklistItem with a `required` field. Out of scope for 3b.
 */
export function toRegistrySpec(spec: AuthoringSpec): JobSpec {
  return {
    ...spec,
    phases: spec.phases.map((p) => ({
      name: p.name,
      label: p.label,
      responsibleTeam: p.responsibleTeam,
      requiredPhotos: [...p.requiredPhotos],
      checklistItems: p.checklistItems
        .filter((c) => c.required)
        .map((c) => ({ id: c.id, label: c.label })),
    })),
    billOfMaterials: spec.billOfMaterials.map((b) => ({ ...b })),
  };
}

// ── Diff / summary ──────────────────────────────────────────────────────────

/** Human-readable summary of differences between two authoring specs. */
export function summarizeChanges(prev: AuthoringSpec, next: AuthoringSpec): string {
  const parts: string[] = [];
  let photosAdded = 0;
  let photosRemoved = 0;
  let checksAdded = 0;
  let checksRemoved = 0;
  let checksRelabeled = 0;
  let requirementToggled = 0;

  for (const np of next.phases) {
    const pp = prev.phases.find((p) => p.name === np.name);
    if (!pp) continue;
    photosAdded += np.requiredPhotos.filter((x) => !pp.requiredPhotos.includes(x)).length;
    photosRemoved += pp.requiredPhotos.filter((x) => !np.requiredPhotos.includes(x)).length;

    const prevById = new Map(pp.checklistItems.map((c) => [c.id, c]));
    const nextById = new Map(np.checklistItems.map((c) => [c.id, c]));
    for (const c of np.checklistItems) {
      const before = prevById.get(c.id);
      if (!before) checksAdded += 1;
      else {
        if (before.label !== c.label) checksRelabeled += 1;
        if (before.required !== c.required) requirementToggled += 1;
      }
    }
    for (const c of pp.checklistItems) {
      if (!nextById.has(c.id)) checksRemoved += 1;
    }
  }

  if (photosAdded) parts.push(`+${photosAdded} photo${photosAdded === 1 ? "" : "s"}`);
  if (photosRemoved) parts.push(`−${photosRemoved} photo${photosRemoved === 1 ? "" : "s"}`);
  if (checksAdded) parts.push(`+${checksAdded} check${checksAdded === 1 ? "" : "s"}`);
  if (checksRemoved) parts.push(`−${checksRemoved} check${checksRemoved === 1 ? "" : "s"}`);
  if (checksRelabeled) parts.push(`${checksRelabeled} relabeled`);
  if (requirementToggled) parts.push(`${requirementToggled} required-flag toggled`);
  return parts.length > 0 ? parts.join(", ") : "no changes";
}

export function specsEqual(a: AuthoringSpec, b: AuthoringSpec): boolean {
  return JSON.stringify(toRegistrySpec(a)) === JSON.stringify(toRegistrySpec(b))
    && JSON.stringify(a.phases.map(p => p.checklistItems)) === JSON.stringify(b.phases.map(p => p.checklistItems));
}

// ── Pure reducers (used by context) ─────────────────────────────────────────

export function appendLog(
  log: JobSpecChangeLogEntry[],
  entry: JobSpecChangeLogEntry,
): JobSpecChangeLogEntry[] {
  return [entry, ...log].slice(0, CHANGE_LOG_CAP);
}

export function nextVersionNumber(
  registryLatest: number,
  publishedVersions: AuthoringSpec[],
): number {
  const overlayLatest = publishedVersions.reduce(
    (max, s) => Math.max(max, s.versionNumber),
    0,
  );
  return Math.max(registryLatest, overlayLatest) + 1;
}

// ── Provider-free overlay resolution (Layer 3b.1) ───────────────────────────
// AppDataProvider sits ABOVE JobSpecAuthoringProvider, so the binding effect
// can't consume the React hook. We read the same localStorage key directly
// to resolve the latest effective canonical for new-job binding. Existing
// BoundJobSpec snapshots are never mutated — they continue to use their
// frozen snapshot via resolveBoundSpec().
//
// IMPORTANT: keep the storage key in sync with JobSpecAuthoringContext.

export const JOBSPEC_AUTHORING_STORAGE_KEY = "jobspec:authoring:v1";

export function readAuthoringRecordsFromStorage(): JobSpecAuthoringRecord[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(JOBSPEC_AUTHORING_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as JobSpecAuthoringRecord[]) : [];
  } catch {
    return [];
  }
}

/**
 * Pure resolver: given a registry-canonical JobSpec and the current overlay
 * records, return the spec to use when binding NEW jobs.
 *
 * Rules:
 *   • If no overlay records for this specId → return base unchanged.
 *   • If overlays exist → return the highest-versioned published overlay,
 *     projected back to registry shape.
 */
export function resolveEffectiveCanonicalSpec(
  base: JobSpec | undefined,
  records: JobSpecAuthoringRecord[],
): JobSpec | undefined {
  if (!base) return base;
  const rec = records.find((r) => r.specId === base.specId);
  if (!rec || rec.publishedVersions.length === 0) return base;
  const latestOverlay = rec.publishedVersions.reduce((a, b) =>
    a.versionNumber >= b.versionNumber ? a : b,
  );
  return toRegistrySpec(latestOverlay);
}
